import { Injectable } from "@angular/core";
import { catchError, map, switchMap } from "rxjs/operators";
import { throwError, Observable, of } from "rxjs";

import { HttpService } from "src/app/services/http.service";
import { InvestorContactReqRes } from "src/app/dashboard/contacts/models/investorContactReqRes.model";
import { ContactTagsResponse } from "src/app/dashboard/contacts/contactTagsResponse.model";
import { AbstractRestService } from "src/app/shared/types/AbstractRestService";
import { ContactSearchOptionsRequest } from "src/app/shared/models/contactSearchOptionsRequest.model";
import { ContactsImportResponse } from "src/app/shared/models/ContactsImportResponse.model";
import { ContactSubscriptionRequest } from "src/app/account/contact-unsubscribe/ContactSubscriptionRequest.model";
import { ContactImportResponse } from "src/app/shared/models/ContactImportResponse.model";
import { BaseResponseDataDto } from "src/app/shared/models/BaseResponseDto.model";
import { InvestingEntityWithRelatedInvestmentsHolding } from "src/app/dashboard/contacts/models/investingEntityWithRelatedInvestmentsHolding.model";

@Injectable()
export class ContactDataService extends AbstractRestService<
  InvestorContactReqRes,
  InvestorContactReqRes
> {
  protected readonly baseEndpoint$ = of("contacts/");

  protected readonly tagsEndpoint$ = this.baseEndpoint$.pipe(
    map((endpoint) => endpoint + "tags")
  );
  protected readonly deleteMultipleEndpoint$ = this.baseEndpoint$.pipe(
    map((endpoint) => endpoint + "deleteMultiple")
  );
  protected readonly exportEndpoint$ = this.baseEndpoint$.pipe(
    map((endpoint) => endpoint + "export")
  );
  protected readonly importContatsEndpoint$ = this.baseEndpoint$.pipe(
    map((endpoint) => endpoint + "import")
  );

  constructor(http: HttpService) {
    super(http);
  }

  protected getSearchOptionsToQuerystring(
    options: ContactSearchOptionsRequest
  ): string {
    let queryString = super.getSearchOptionsToQuerystring(options);
    const tagQuerystring = (options.tags || [])
      .map((tag) => {
        return `&tags=${tag}`;
      })
      .join("");

    if (tagQuerystring) {
      queryString += tagQuerystring;
    }

    if (options.fundraisingId > 0) {
      queryString += `&fundraisingId=${options.fundraisingId}`;
    }

    if (options.contactReferrerId) {
      queryString += `&contactReferrerId=${options.contactReferrerId}`;
    }

    if (options.excludeAgentContacts) {
      queryString += `&excludeAgentContacts=${options.excludeAgentContacts}`;
    }
    if (options.isBaseSearch) {
      queryString += `&isBaseSearch=${options.isBaseSearch}`;
    }

    return queryString;
  }

  getContactTags(): Observable<ContactTagsResponse> {
    return this.tagsEndpoint$.pipe(
      switchMap((tagsEndpoint) =>
        this.http.getTyped<ContactTagsResponse>(tagsEndpoint)
      ),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  deleteMultiple(ids: number[]): Observable<BaseResponseDataDto<InvestingEntityWithRelatedInvestmentsHolding[]>> {
    return this.deleteMultipleEndpoint$.pipe(
      switchMap((endpoint) => this.http.postTyped<BaseResponseDataDto<InvestingEntityWithRelatedInvestmentsHolding[]>>(endpoint, ids)),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  deleteSingle(id: number): Observable<BaseResponseDataDto<InvestingEntityWithRelatedInvestmentsHolding[]>> {
    return this.baseEndpoint$.pipe(
      switchMap(endpoint => this.http.deleteTyped<BaseResponseDataDto<InvestingEntityWithRelatedInvestmentsHolding[]>>(endpoint + id)),
      catchError(err => {
        return throwError(err);
      })
    );
  }

  exportContacts(options: ContactSearchOptionsRequest): Observable<any> {
    return this.exportEndpoint$.pipe(
      switchMap((endpoint) =>
        this.http.get(
          `${endpoint}${this.getSearchOptionsToQuerystring(options)}`
        )
      )
    );
  }

  importContacts(
    metaFileLinkId: number,
    contactTags: string[] = null
  ): Observable<ContactsImportResponse> {
    return this.importContatsEndpoint$.pipe(
      switchMap((endpoint) =>
        this.http.postTyped<ContactsImportResponse>(endpoint, {
          metaFileLinkId,
          contactTags,
        })
      ),
      catchError((err) => {
        return throwError(err);
      })
    );
  }

  GetContactSubscriptionState(
    contactId: number,
    uniqueIdentifier: string
  ): Observable<ContactSubscriptionRequest> {
    return this.baseEndpoint$.pipe(
      switchMap((baseEndpoint) =>
        this.http
          .getTyped<ContactSubscriptionRequest>(
            `${baseEndpoint}/${contactId}/subscription/${uniqueIdentifier}`
          )
          .pipe(
            catchError((err) => {
              return throwError(err);
            })
          )
      )
    );
  }

  UpdateContactSubscription(
    contactId: number,
    uniqueIdentifier: string,
    contactSubscriptionRequest: ContactSubscriptionRequest
  ): Observable<ContactSubscriptionRequest> {
    return this.baseEndpoint$.pipe(
      switchMap((baseEndpoint) =>
        this.http
          .putTyped<ContactSubscriptionRequest>(
            `${baseEndpoint}/${contactId}/subscription/${uniqueIdentifier}`,
            contactSubscriptionRequest
          )
          .pipe(
            catchError((err) => {
              return throwError(err);
            })
          )
      )
    );
  }

  createContactsAndInvestingEntitiesFromExcelFile(
    metaFileLinkId: number
  ): Observable<ContactImportResponse> {
    return this.baseEndpoint$.pipe(
      switchMap((endpoint) =>
        this.http.postTyped<ContactImportResponse>(
          `${endpoint}import/${metaFileLinkId}`,
          null
        )
      ),
      catchError((err) => {
        return throwError(err);
      })
    );
  }
}
